import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import GenericState from '../../MyComponents/GenericLoadingErrorState/GenericState';
import ShareLessonIcon from '../../MyComponents/icons/ShareLessonIcon';
import MembersTable from './MembersTable';
import RequestsTable from './RequestsTable';
import MultiBoardLessonListTable from './MultiBoardLessonListTable';
import useEditStyles from './editStyles';
import classApi from '../../Api/classApi';
import store from '../../redux/store';
import { lessonInfo, shareLesson, showSuccess } from '../../redux/actions';
import { loadListingFromStorageInStore } from '../../common/utils';
import { GA_EVENT_TREE } from '../../gaConstants';
import { MANAGE_CLASSES_LINK } from '../../common/urlLinks';
import { KICKER, TEXT_LINK } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const useStyles = makeStyles(() => ({
    back: {
        ...TEXT_LINK,
        marginBottom: '18px',
    },
    head: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '16px 24px',
        marginBottom: '28px',
    },
    kicker: {
        ...KICKER,
        marginBottom: '8px',
    },
    title: {
        margin: 0,
        fontSize: 'clamp(1.7rem, 3.2vw, 2.5rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        overflowWrap: 'anywhere',
    },
}));

/**
 * The group edit page: the group's name and description, the participants'
 * join requests, its members and its saved individual-boards sessions. Name
 * and description save when their field is left or the save button is pressed.
 */
const EditClass = ({ match, profile, appState }) => {
    const classes = useStyles();
    const edit = useEditStyles();
    const history = useHistory();
    const classId = match.params.classId;

    const [name, setName] = useState('');
    const [savedName, setSavedName] = useState('');
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [reload, setReload] = useState(false);
    // Kept as the server sent them, so a save does not clear them.
    const [state, setState] = useState({
        disableMembersDrawDefault: false,
        muteMembersDefault: true,
        allowMembersToStart: false,
    });

    const serverVals = useRef({ name: null, description: null, disableMembersDrawDefault: false });

    useEffect(() => {
        classApi
            .getClass(classId)
            .then((res) => {
                if (!res.data.id) {
                    console.log('no class available');
                    setIsLoading(false);
                    setIsError(true);
                }
                store.dispatch(lessonInfo(res.data));
                setDescription(res.data.description ? res.data.description : '');
                setName(res.data.name);
                setSavedName(res.data.name);
                serverVals.current = {
                    name: res.data.name,
                    description: res.data.description,
                    disableMembersDrawDefault: res.data.disableMembersDrawDefault,
                };
                setIsLoading(false);
                setState({
                    disableMembersDrawDefault: res.data.disableMembersDrawDefault,
                    muteMembersDefault: res.data.muteMembersDefault,
                    allowMembersToStart: res.data.allowMembersToStart,
                });

                loadListingFromStorageInStore(profile.email, classId);
            })
            .catch((err) => {
                setIsLoading(false);
                setIsError(true);
                console.log(err);
            });
    }, [classId, profile.email]);

    const updateClass = (dd = null) => {
        if (!dd) {
            dd = { name, description, ...state };
        }
        if (
            dd.name !== serverVals.current.name ||
            dd.description !== serverVals.current.description ||
            dd.disableMembersDrawDefault !== serverVals.current.disableMembersDrawDefault
        ) {
            setIsLoading(true);
            window.logAppActivity(GA_EVENT_TREE.editGroup.actions.updateMeta);
            classApi.updateClass(classId, dd).then(() => {
                setIsLoading(false);
                serverVals.current = dd;
                setSavedName(dd.name);
                store.dispatch(showSuccess('SUCCESSFUL_UPDATE'));
            });
        }
    };

    const save = (event) => {
        event.preventDefault();
        updateClass();
    };

    const start = () => {
        window.logAppActivity(GA_EVENT_TREE.editGroup.actions.start);
        history.push('/lesson/' + classId);
    };

    const share = () => store.dispatch(shareLesson({ id: classId, name: savedName }));

    if (isError) {
        return <GenericState isError={true} />;
    }

    const dirty =
        name !== serverVals.current.name ||
        description !== (serverVals.current.description ? serverVals.current.description : '');

    return (
        <Grid item xs={12}>
            <LandingSection id="top" background="none" gutter={0} paddingTop={8} paddingBottom={56}>
                <Link to={MANAGE_CLASSES_LINK} className={classes.back}>
                    <span aria-hidden="true">←</span>
                    <FormattedMessage id="redesign.groups.title" />
                </Link>

                <div className={classes.head}>
                    <div>
                        <span className={classes.kicker}>
                            <FormattedMessage id="redesign.updateClass.edit" />
                        </span>
                        <h1 className={classes.title}>{savedName}</h1>
                    </div>
                    <div className={edit.actions}>
                        <button type="button" onClick={share} className={edit.secondary}>
                            <ShareLessonIcon />
                            <FormattedMessage id="generic.label.shareLesson" />
                        </button>
                        <button type="button" onClick={start} className={edit.primary}>
                            <FormattedMessage id="generic.label.startLesson" />
                            <span aria-hidden="true">→</span>
                        </button>
                    </div>
                </div>

                <form className={edit.card} onSubmit={save}>
                    <div className={edit.cardHead}>
                        <h2 className={edit.cardTitle}>
                            <FormattedMessage id="updateClass.classSettings" />
                        </h2>
                    </div>
                    <label className={edit.field}>
                        <span className={edit.label}>
                            <FormattedMessage id="redesign.createGroup.className" />
                        </span>
                        <input
                            type="text"
                            name="name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            onBlur={() => updateClass()}
                            maxLength={120}
                            disabled={isLoading}
                            className={edit.input}
                        />
                    </label>
                    <label className={edit.field}>
                        <span className={edit.label}>
                            <FormattedMessage id="createGroup.description" />
                        </span>
                        <textarea
                            name="description"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            onBlur={() => updateClass()}
                            rows={4}
                            disabled={isLoading}
                            className={edit.textarea}
                        />
                    </label>
                    <div className={edit.actions}>
                        <button type="submit" disabled={isLoading || !dirty} className={edit.primary}>
                            <FormattedMessage id="redesign.updateClass.save" />
                        </button>
                        <p className={edit.hint} style={{ margin: 0 }}>
                            <FormattedMessage id="redesign.updateClass.settingsHint" />
                        </p>
                    </div>
                </form>

                <RequestsTable classId={classId} onReload={() => setReload(!reload)} />

                <MembersTable classId={classId} reload={reload} />

                <div className={edit.card}>
                    <MultiBoardLessonListTable
                        list={appState.multiBoardState}
                        profileEmail={profile.email}
                        lessonId={classId}
                        lessonName={savedName}
                    />
                </div>
            </LandingSection>
        </Grid>
    );
};

const mapStateToProps = (state) => {
    const { profile, appState } = state;
    return { profile, appState };
};

export default connect(mapStateToProps)(EditClass);
